<template>
    <div class="card oc-advanced-filters" v-class="{'with-toggle': $_useToggleOption}">
        <oc-form :model="form" :nested="true">
            <transition
                @enter="enterTransition"
                @leave="leaveTransition"
                :css="false"
                v-if="Object.keys(this.filters).length > 0"
            >
                <div class="card-header" v-if="useToggleOption && expandedAdvancedSearch">
                    <h6>{{titleText}}</h6>

                    <div class="btn-group-toggle">
                        <oc-label-input
                            v-for="(item, index) in $_filterButtons"
                            :key="index"
                            class="btn btn-sm btn-outline-info oc-af-btn-flt-activate"
                            :class="{active: isActive(item.field)}"
                            v-if="isVisible(item)"
                        >
                            <oc-checkbox-input autocomplete="off" :data="activeFilters" @input="onActiveChange" :value="item.field" /> {{getLabel(item)}}
                        </oc-label-input>
                    </div>
                </div>
            </transition>

            <div class="card-body">
                <oc-row v-if="!activeFilters.length">
                    <oc-col>
                        <oc-no-results-found :text="noActiveFiltersText" />
                    </oc-col>
                </oc-row>

                <oc-row v-for="rowNum in getRows" :key="'row-' + rowNum">
                    <oc-col :size="columnSize" v-for="item in getRowChunk(rowNum)" :key="'col-' + item.field" v-if="isVisible(item)">
                        <component
                            v-if="isActive(item.field)"
                            :is="item.component"
                            v-bind="item.options"
                            v-on="item.events"
                            v-model="form[item.field]"
                        />
                    </oc-col>
                </oc-row>

                <hr>

                <div class="oc-af-footer">
                    <slot name="buttons">
                        <slot name="buttons-before"></slot>
                        <oc-button
                            v-for="(button, index) in $_buttons"
                            :key="index"
                            v-bind="button.attrs"
                            v-class="button.classes"
                            v-on="button.events"
                        />
                        <slot name="buttons-after"></slot>
                    </slot>
                </div>

                <oc-button
                    v-if="$_useToggleOption"
                    type="primary"
                    icon="filter"
                    size="small"
                    :title="toggleButtonText"
                    @click="toggle"
                    class="oc-af-collapse-btn"
                />
            </div>
        </oc-form>
    </div>
</template>

<script>
import Velocity from 'velocity'
import Form from '@/helpers/Form'

import DateTimeRangeInput from '@/components/ui/filter/AdvancedSearch/predefined/DateTimeRangeInput'
import DateTimeInput from '@/components/ui/filter/AdvancedSearch/predefined/DateTimeInput'
import DateInput from '@/components/ui/filter/AdvancedSearch/predefined/DateInput'

export default {
    name: 'oc-advanced-search',
    props: {
        model: {
            type: [Object, Boolean],
            default: false
        },
        /**
         * @example
         *
         * [
         *     {
         *         field: "from_date",
         *         label: "Some Label", // (optional)
         *         options: {
         *             // all <oc-form-field> options goes here
         *         },
         *         events: {
         *             // all <oc-form-field> events goes here
         *         },
         *         component: "oc-form-field", // by default
         *         active: false // by default
         *         visible: true // by default boolean|function,
         *         predefined: undefined // optional
         *     }
         * ]
         *
         * @type {Array}
         */
        items: {
            type: Array,
            required: true
        },
        startOpened: {
            type: Boolean,
            default: false
        },
        /**
         * Specified fields cannot be deactivated - they are always active
         */
        lockedFields: {
            type: Array,
            default: () => []
        },
        itemsPerRow: {
            type: [Number, String],
            default: 3
        },
        useToggleOption: {
            type: Boolean,
            default: true
        },
        /**
         * Parameters are the same as in <oc-button />
         * Example:
         * [
         *   {
         *       text: 'Cancel'
         *       on: {
         *           click: {
         *               // do something
         *           }
         *       }
         *   },
         *   {
         *       text: 'Export',
         *       type: 'primary',
         *       tag: 'a',
         *       ...
         *   }
         * ]
         *
         * If boolean FALSE is given - default buttons will be loaded
         *
         * @type {Object}
         */
        buttons: {
            type: [Array, Boolean, Function],
            default: false
        },
        resetButtonText: {
            type: String,
            default: 'Reset'
        },
        searchButtonText: {
            type: String,
            default: 'Search'
        },
        titleText: {
            type: String,
            default: 'Filter by:'
        },
        toggleButtonText: {
            type: String,
            default: 'Add more filers...'
        },
        noActiveFiltersText: {
            type: String,
            default: 'There are no active filters.'
        },
        useRouter: {
            type: Boolean,
            default: true
        },
        filterParam : {
            type: String,
            default: 'flt_'
        },
        predefinedOptions: {
            type: Object
        }
    },
    watch: {
        form: {
            immediate: true,
            deep: true,
            handler() {
                this.handleFilters()
            }
        },
        items: {
            immediate: true,
            handler() {
                this.handleFilters()
            }
        },
        $route: {
            handler() {
                this.scanQueryString(true)
            }
        }
    },
    created() {
        this.scanQueryString(false)
    },
    data() {
        let form = this.model
        if( false === form ) {
            form = new Form()
        }

        return {
            expandedAdvancedSearch: true,
            activeFilters: [],
            filters: {},
            filtersLength: 0,
            form,
            predefinedFilters: {
                [DateTimeRangeInput.name]: DateTimeRangeInput,
                [DateTimeInput.name]: DateTimeInput,
                [DateInput.name]: DateInput
            }
        }
    },
    computed: {
        columnSize() {
            let maxCols = 12 // bs grid cols
            return maxCols/this.itemsPerRow
        },
        getRows() {
            return Math.ceil(this.activeFilters.length / parseInt(this.itemsPerRow))
        },
        $_buttons() {
            let buttonsRaw = []

            if( false === this.buttons || typeOf(this.buttons) === 'function' ) {
                buttonsRaw = [
                    {
                        text: this.resetButtonText,
                        type: 'default',
                        size: 'small',
                        tag: 'a',
                        on: {
                            click: this.onReset
                        }
                    },
                    {
                        text: this.searchButtonText,
                        type: 'primary',
                        size: 'small',
                        tag: 'a',
                        icon: 'search',
                        on: {
                            click: this.onFilter
                        }
                    }
                ]

                if( typeOf(this.buttons) === 'function' ) {
                    buttonsRaw = this.buttons(buttonsRaw)
                }
            } else {
                buttonsRaw = this.buttons
            }

            let buttons = []

            for( let button of buttonsRaw ) {
                let attrs = Object.assign({}, button)
                let events = attrs.on
                let classes = attrs.class
                delete attrs.on
                delete attrs.class

                buttons.push({
                    attrs,
                    events,
                    classes
                })
            }

            return buttons
        },
        $_filterButtons() {
            let buttons = {}
            for( let field in this.filters ) {
                if( ! inArray(field, this.lockedFields) ) {
                    buttons[field] = this.filters[field]
                }
            }
            return buttons
        },
        $_useToggleOption() {
            return this.useToggleOption && Object.keys(this.$_filterButtons).length > 0
        },
        $_predefinedOptions() {
            return Object.assign({
                DateInput: {},
                DateTimeInput: {},
                DateTimeRangeInput: {
                    separator: ' - '
                }
            }, this.predefinedOptions)
        }
    },
    methods: {
        handleFilters()
        {
            let activeFilters = []
            let filtersLength = 0
            let filters = {}

            for( let i in this.items )
            {
                let x = this.items[i]

                let cloned
                if( isObject(x) ) {
                    cloned = Object.assign({}, x)
                } else {
                    cloned = {field: x}
                }

                cloned = Object.assign({
                    label: null,
                    component: "oc-form-field",
                    options: {},
                    events: {}
                }, cloned)

                let clonedOptions = cloned.options
                if( typeof clonedOptions === 'function' ) {
                    clonedOptions = clonedOptions()
                }

                cloned.options = Object.assign({
                    name: cloned.field,
                    indicationInvalid: false,
                    indicationValid: false,
                    inputDebounce: 0,
                    placeholderPrefix: "Search ",
                    size: "small",
                    class: "oc-af-filter-input",
                    theme: 'default'
                }, clonedOptions)

                if( cloned.predefined !== undefined && this.predefinedFilters.hasOwnProperty(cloned.predefined) )
                {
                    let predefined = this.predefinedFilters[cloned.predefined].filter(cloned.options.name, this.form, this.$_predefinedOptions[cloned.predefined])

                    let predefinedOptions = predefined.options

                    if( typeof predefinedOptions === 'function' ) {
                        predefinedOptions = predefinedOptions()
                    }
                    cloned.options = Object.assign(cloned.options, predefinedOptions)
                    cloned.events = Object.assign(cloned.events, predefined.events)
                }

                if( cloned.options.$mode === undefined ) {
                    cloned.options.$mode = 'edit'
                }

                if( cloned.options.label === undefined ) {
                    cloned.options.label = cloned.label
                }

                if( (inArray(cloned.field, this.lockedFields) || cloned.active) && activeFilters.indexOf(cloned.field) === -1 ) {
                    activeFilters.push(cloned.field)
                    delete cloned.active
                }

                filters[cloned.field] = cloned
                filtersLength++
            }

            this.activeFilters = activeFilters
            this.filtersLength = filtersLength
            this.filters = filters
            this.expandedAdvancedSearch = this.startOpened || activeFilters.length === 0
        },
        onActiveChange(activeFilters, target) {
            this.activeFilters = activeFilters

            this.$emit(
                'filter-' + (! this.isActive(target) ? 'deactivation' : 'activation'),
                target
            )

            if( activeFilters.indexOf(target) === -1 && this.form.has(target) ) {
                this.form.set(target, '')
            }
        },
        registerActiveFilter(filterName) {
            if( ! this.isActive(filterName) ) {
                this.activeFilters.push(filterName)
            }
        },
        scanQueryString(resetPaging = false) {
            if ( ! this.useRouter ) {
                return
            }

            setTimeout(() => {
                let hasChange = false

                for( let key in this.$route.query )
                {
                    if( key.indexOf(this.filterParam) === 0 )
                    {
                        let parsedKey = key.substr(this.filterParam.length)
                        let value = this.$route.query[key]

                        if( this.form.has(parsedKey) && String(this.form[parsedKey]) !== String(value) )
                        {
                            hasChange = true
                            this.form.set(parsedKey, value)

                            if( this.filters.hasOwnProperty(parsedKey) ) {
                                this.registerActiveFilter(parsedKey)
                            }
                        }
                    }
                }

                let formData = this.form.all()
                for( let key in formData )
                {
                    if( this.$route.query[this.filterParam + key] === undefined && formData[key] !== '' && !this.isActive(key) && formData !== undefined ) {
                        this.form.set(key, '')
                    }
                }

                if( hasChange )
                {
                    this.onFilter(resetPaging)
                }
            }, 0)
        },
        onFilter(resetPaging = true) {

            let appliedFilters = this.form.getData()

            if ( this.useRouter ) {
                let queryParams = Object.assign({}, this.$route.query)

                for( let param in appliedFilters ) {
                    let queryKey = this.filterParam + param
                    let value = appliedFilters[param]

                    if( value === '' || value === undefined ) {
                        delete queryParams[queryKey]
                    } else {
                        queryParams[queryKey] = value
                    }
                }

                this.$router.replace({query: queryParams})
            }

            for( let param in appliedFilters ) {
                if( appliedFilters[param] === '' || appliedFilters[param] === undefined ) {
                    delete appliedFilters[param]
                }
            }

            if( resetPaging && toJson(this.currentFilters) !== toJson(appliedFilters) ) {
                this.$emit('change', appliedFilters) // filters are changed
            }

            this.currentFilters = appliedFilters

            this.$emit('filter', appliedFilters)
        },
        onReset() {
            this.form.keys().forEach( key => {
                this.form.set(key, '')
            })

            this.onFilter()
        },
        isVisible(item) {
            if( typeof item.visible === 'function' ) {
                return item.visible()
            }

            return item.visible === undefined || item.visible
        },
        isActive(filterName) {
            return this.activeFilters.indexOf(filterName) !== -1
        },
        toggle() {
            this.expandedAdvancedSearch = !this.expandedAdvancedSearch
        },
        getRowChunk(rowNum) {
            let itemsPerRow = parseInt(this.itemsPerRow)

            let chunkActive = this.activeFilters.slice(
                (rowNum - 1) * itemsPerRow,
                rowNum * itemsPerRow
            )

            let chunk = []
            for (let activeFilter of chunkActive ) {
                chunk.push(this.filters[activeFilter])
            }

            return chunk
        },
        getLabel(item) {
            if(item.label) {
                return item.label
            }
            return this.form.getFieldLabel(item.field)
        },
        enterTransition (el, done) {
            Velocity(el, "slideDown", { duration: 350, complete: done, easing: 'ease-in-out' })
        },
        leaveTransition (el, done) {
            Velocity(el, "slideUp", { duration: 350, complete: done, easing: 'ease-in-out' })
        }
    }
}
</script>

<style scoped>
.oc-advanced-filters.with-toggle {
    margin-top:18px;
}
.oc-advanced-filters {
    max-width:75%;
    background:#eee;
}
.oc-advanced-filters .card-body {
    position: relative;
}
.oc-af-collapse-btn {
    position:absolute;
    top: -16px;
    right: -14px;
}
.oc-af-btn-flt-activate {
    padding:.15rem .5rem;
    margin-right: 6px;
}
.oc-af-footer>:not(:last-child) {
    margin-right: .25rem;
}
</style>
